import { LegalLayout, Section, Highlight } from "./_LegalLayout";
import { SEO } from "@/lib/seo";

export default function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy · Fieldlink NZ"
        description="How Fieldlink handles personal information under the New Zealand Privacy Act 2020 — collection, use, disclosure, security and your rights."
        path="/privacy"
      />
      <LegalLayout
        tag="/ Legal"
        title="Fieldlink Privacy Policy"
        intro="How we collect, hold, use, disclose and protect personal information when you use Fieldlink's Services."
        lastUpdated="14 September 2026"
      >
        <Section id="about" title="1. About this policy">
          <p>
            Fieldlink (&quot;Fieldlink&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) provides cloud-based software and related services for civil construction businesses, contractors, workers, suppliers and other organisations.
          </p>
          <p>We take privacy and information security seriously.</p>
          <p>
            This Privacy Policy explains how we collect, hold, use, disclose and protect personal information when you use Fieldlink&apos;s websites, applications, mobile applications, portals, integrations, support services and related services (together, the &quot;Services&quot;).
          </p>
          <p>We handle personal information in accordance with the New Zealand Privacy Act 2020 and other applicable laws.</p>
          <p>This Privacy Policy should be read together with the Fieldlink Terms of Service and any other agreement that applies to your use of Fieldlink.</p>
        </Section>

        <Section id="who-this-applies-to" title="2. Who this policy applies to">
          <p>This Policy may apply to information relating to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Fieldlink account holders;</li>
            <li>employees and workers of Fieldlink customers;</li>
            <li>contractors and subcontractors;</li>
            <li>customers and suppliers;</li>
            <li>drivers and plant operators;</li>
            <li>site visitors;</li>
            <li>contacts entered into Fieldlink;</li>
            <li>users of customer or contractor portals;</li>
            <li>people appearing in documents, photographs, forms or records uploaded to Fieldlink;</li>
            <li>people whose information is otherwise entered, collected or processed through the Services.</li>
          </ul>
          <p>
            Where an organisation uses Fieldlink to collect or manage information about its workers, contractors, customers, suppliers or other individuals, that organisation may also have its own privacy obligations.
          </p>
        </Section>

        <Section id="information-we-collect" title="3. Information we may collect">
          <p>Depending on the Fieldlink features used, information processed through Fieldlink may include:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>name;</li>
            <li>email address;</li>
            <li>phone number;</li>
            <li>account and profile information;</li>
            <li>employer or organisation;</li>
            <li>job title and role;</li>
            <li>employment and workforce information;</li>
            <li>timesheets and working hours;</li>
            <li>leave information;</li>
            <li>payroll-related information;</li>
            <li>competency, qualification and training records;</li>
            <li>job and project information;</li>
            <li>site records;</li>
            <li>health and safety records;</li>
            <li>toolbox talk and sign-on records;</li>
            <li>pre-start and inspection records;</li>
            <li>forms and checklists;</li>
            <li>photographs and uploaded documents;</li>
            <li>signatures or sign-on confirmations;</li>
            <li>vehicle and plant information;</li>
            <li>driver and truck information;</li>
            <li>GPS or location information where relevant Fieldlink functionality is enabled;</li>
            <li>supply-yard visits and material movements;</li>
            <li>weighbridge and docket information;</li>
            <li>customer and supplier information;</li>
            <li>quotes, invoices and financial records;</li>
            <li>communications and correspondence;</li>
            <li>support requests;</li>
            <li>device, browser and technical information;</li>
            <li>IP address;</li>
            <li>authentication and security information;</li>
            <li>application activity and audit records;</li>
            <li>integration information;</li>
            <li>other information entered, uploaded or generated through the Services.</li>
          </ul>
          <p>The information collected depends on the Fieldlink modules and functionality being used.</p>
        </Section>

        <Section id="customer-data" title="4. Customer data">
          <p>
            Businesses using Fieldlink may upload, enter, generate, import or otherwise provide information to the Services (&quot;Customer Data&quot;).
          </p>
          <p>
            The customer remains responsible for determining what Customer Data its authorised users enter into Fieldlink and for ensuring that it has an appropriate lawful basis for collecting and using that information.
          </p>
          <p>
            Fieldlink processes Customer Data for the purpose of providing, securing, maintaining, supporting and improving the Services and as otherwise permitted by our agreement with the customer and applicable law.
          </p>
          <p>Fieldlink does not acquire ownership of Customer Data merely because that information is stored or processed using Fieldlink.</p>
        </Section>

        <Section id="why-we-collect" title="5. Why we collect and use information">
          <p>We may collect and use information where reasonably necessary to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>provide the Services;</li>
            <li>create and administer accounts;</li>
            <li>authenticate users;</li>
            <li>operate jobs and project-management functionality;</li>
            <li>process timesheets and workforce records;</li>
            <li>operate safety and compliance functionality;</li>
            <li>manage plant, vehicles and assets;</li>
            <li>provide supply-yard, docket and material-management functionality;</li>
            <li>provide quoting, invoicing and financial functionality;</li>
            <li>generate documents and reports;</li>
            <li>provide integrations requested or enabled by customers;</li>
            <li>provide customer support;</li>
            <li>communicate service-related information;</li>
            <li>detect, investigate and prevent misuse, fraud and security threats;</li>
            <li>maintain audit and security records;</li>
            <li>diagnose technical issues;</li>
            <li>monitor and improve the performance and reliability of Fieldlink;</li>
            <li>comply with legal obligations;</li>
            <li>enforce our agreements and protect our legal rights;</li>
            <li>perform other purposes authorised by the individual concerned or permitted by law.</li>
          </ul>
          <p>We will not collect personal information where it is not reasonably necessary for a lawful purpose connected with our activities.</p>
        </Section>

        <Section id="info-from-customers" title="6. Information provided by Fieldlink customers">
          <p>Much of the information held within Fieldlink is entered by businesses and their authorised users.</p>
          <p>
            If your information has been entered into Fieldlink by your employer, contractor, principal or another organisation, that organisation may be responsible for deciding why the information was collected and how it is used.
          </p>
          <p>Requests concerning that information may therefore need to be handled by that organisation.</p>
          <p>Fieldlink will provide reasonable assistance where appropriate and required by law.</p>
        </Section>

        <Section id="access-to-information" title="7. Access to information">
          <p>Access to Customer Data within Fieldlink is controlled through user accounts, roles and permissions.</p>
          <p>Customers are responsible for:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>deciding who should have access to their Fieldlink account;</li>
            <li>assigning appropriate permissions;</li>
            <li>maintaining accurate user access;</li>
            <li>removing access when it is no longer required;</li>
            <li>protecting account credentials;</li>
            <li>ensuring users do not share passwords or authentication credentials;</li>
            <li>promptly notifying Fieldlink of suspected unauthorised access.</li>
          </ul>
          <p>Users must not attempt to access information they are not authorised to access.</p>
        </Section>

        <Section id="security" title="8. Security">
          <p>
            Fieldlink uses technical and organisational measures designed to protect information against loss, unauthorised access, disclosure, alteration, misuse and destruction.
          </p>
          <p>Security measures may include, where appropriate:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>authentication and access controls;</li>
            <li>role-based permissions;</li>
            <li>encryption;</li>
            <li>secure communications;</li>
            <li>logging and monitoring;</li>
            <li>audit records;</li>
            <li>backups;</li>
            <li>infrastructure security;</li>
            <li>vulnerability and dependency management;</li>
            <li>administrative controls;</li>
            <li>incident response procedures;</li>
            <li>access restrictions for Fieldlink personnel.</li>
          </ul>
          <p>
            However, no internet-connected service, computer system, database, network, cloud platform, transmission method or electronic storage system can be guaranteed to be completely secure.
          </p>
          <p>
            Accordingly, while Fieldlink takes reasonable measures to protect information, Fieldlink does not represent or warrant that the Services will be immune from all cyberattacks, unauthorised access, malicious activity, software vulnerabilities, credential compromise, infrastructure failure or other security incidents.
          </p>
          <p>Nothing in this section limits any obligation Fieldlink has under applicable law.</p>
        </Section>

        <Section id="customer-security-responsibilities" title="9. Customer security responsibilities">
          <p>Security is a shared responsibility.</p>
          <p>Customers and users are responsible for taking reasonable steps to protect their accounts and information, including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>using strong passwords;</li>
            <li>protecting login credentials;</li>
            <li>using available authentication security features;</li>
            <li>not sharing user accounts;</li>
            <li>maintaining appropriate user permissions;</li>
            <li>promptly disabling former employees or users;</li>
            <li>maintaining secure devices and networks;</li>
            <li>ensuring information entered into Fieldlink is appropriate and lawful;</li>
            <li>reviewing connected integrations;</li>
            <li>promptly reporting suspected security incidents.</li>
          </ul>
          <p>
            Fieldlink is not responsible for unauthorised access resulting solely from a customer&apos;s or user&apos;s acts or omissions where Fieldlink has complied with its own legal and contractual obligations.
          </p>
        </Section>

        <Section id="privacy-security-incidents" title="10. Privacy and security incidents">
          <p>A &quot;privacy breach&quot; has the meaning given to that term under applicable privacy law.</p>
          <p>If Fieldlink becomes aware of a suspected privacy or security incident affecting personal information, we may:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>investigate the incident;</li>
            <li>take reasonable steps to contain and remediate it;</li>
            <li>assess the nature and extent of affected information;</li>
            <li>assess the risk of harm;</li>
            <li>work with relevant service providers, customers and authorities;</li>
            <li>preserve relevant security and audit information; and</li>
            <li>make notifications where required by applicable law.</li>
          </ul>
          <p>
            Where a privacy breach is a notifiable privacy breach under the Privacy Act 2020, Fieldlink will comply with applicable notification requirements.
          </p>
          <p>Fieldlink may contact affected customers or users where reasonably necessary to investigate or respond to an incident.</p>
          <p>Customers must reasonably cooperate with Fieldlink where an incident involves their account, users, systems or integrations.</p>
        </Section>

        <Section id="no-system-completely-secure" title="11. No system is completely secure">
          <p>The parties acknowledge that modern cloud services necessarily involve security risks.</p>
          <p>Despite reasonable safeguards, information may potentially be affected by events including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>sophisticated cyberattacks;</li>
            <li>zero-day vulnerabilities;</li>
            <li>compromised user credentials;</li>
            <li>phishing;</li>
            <li>malware;</li>
            <li>malicious insiders;</li>
            <li>third-party service provider incidents;</li>
            <li>internet or telecommunications failures;</li>
            <li>software defects;</li>
            <li>infrastructure failures;</li>
            <li>events outside Fieldlink&apos;s reasonable control.</li>
          </ul>
          <p>This acknowledgement does not exclude or limit any rights or obligations that cannot lawfully be excluded or limited.</p>
          <p>
            Any contractual allocation or limitation of liability relating to the Services is governed by the Fieldlink Terms of Service or applicable customer agreement.
          </p>
        </Section>

        <Section id="service-providers" title="12. Service providers">
          <p>Fieldlink may use trusted third-party service providers to help operate the Services.</p>
          <p>These may include providers of:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>cloud infrastructure;</li>
            <li>databases and storage;</li>
            <li>authentication;</li>
            <li>email and communications;</li>
            <li>payment processing;</li>
            <li>analytics and monitoring;</li>
            <li>mapping and location services;</li>
            <li>artificial intelligence functionality;</li>
            <li>accounting and business integrations;</li>
            <li>customer support;</li>
            <li>security and infrastructure services.</li>
          </ul>
          <p>
            Service providers may process information only as necessary for the services they provide to Fieldlink, subject to applicable contractual and legal requirements.
          </p>
        </Section>

        <Section id="overseas-processing" title="13. Overseas processing and storage">
          <p>
            Some Fieldlink service providers or infrastructure may be located outside New Zealand, or may process or store information outside New Zealand.
          </p>
          <p>
            Where personal information is disclosed outside New Zealand, Fieldlink will take steps required by applicable privacy law concerning overseas disclosure and protection of personal information.
          </p>
          <p>
            Where an overseas provider processes information on Fieldlink&apos;s behalf as an agent or service provider, Fieldlink will manage that relationship in accordance with applicable legal requirements.
          </p>
          <p>The countries in which information is processed may change as Fieldlink&apos;s infrastructure and service providers evolve.</p>
          <p>
            Where appropriate, further information about material service providers may be made available through Fieldlink&apos;s security or subprocessors information.
          </p>
        </Section>

        <Section id="integrations" title="14. Integrations">
          <p>Customers may connect Fieldlink to third-party services.</p>
          <p>Examples may include accounting, communications, mapping, storage or other business platforms.</p>
          <p>
            When a customer enables an integration, information may be exchanged between Fieldlink and that provider as necessary to provide the integration.
          </p>
          <p>The third-party provider&apos;s own terms and privacy practices may apply to information processed independently by that provider.</p>
          <p>
            Customers are responsible for deciding which integrations they enable and ensuring they are authorised to provide relevant information to those services.
          </p>
        </Section>

        <Section id="artificial-intelligence" title="15. Artificial intelligence">
          <p>
            Where Fieldlink provides artificial intelligence or automated assistance features, information submitted to those features may be processed by Fieldlink and relevant technology providers for the purpose of providing the requested functionality.
          </p>
          <p>Fieldlink will configure and operate such functionality in accordance with applicable privacy obligations and relevant contractual arrangements.</p>
          <p>
            AI-generated content may contain errors and should not be treated as a substitute for professional judgement, site verification, engineering advice, legal advice or safety-critical decision-making.
          </p>
        </Section>

        <Section id="disclosure" title="16. Disclosure of information">
          <p>Fieldlink may disclose personal information where reasonably necessary:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>to provide the Services;</li>
            <li>to service providers acting on our behalf;</li>
            <li>to integrations authorised by a customer;</li>
            <li>to a customer&apos;s authorised users;</li>
            <li>where authorised by the individual concerned;</li>
            <li>where required or permitted by law;</li>
            <li>to protect the security, rights or property of Fieldlink, our customers, users or others;</li>
            <li>in connection with a merger, acquisition, financing, restructuring or sale of all or part of Fieldlink&apos;s business, subject to appropriate protections;</li>
            <li>for another purpose permitted under applicable privacy law.</li>
          </ul>
          <p>We do not sell personal information as a data-brokerage business.</p>
        </Section>

        <Section id="retention" title="17. Retention">
          <p>
            Fieldlink retains personal information for as long as reasonably necessary for the purposes for which it was collected, to provide the Services, to meet contractual obligations, maintain legitimate business and audit records, resolve disputes, enforce agreements, or comply with legal obligations.
          </p>
          <p>Retention periods may differ depending on the type of information and the Fieldlink module involved.</p>
          <p>
            Customers may also have legal obligations requiring certain construction, employment, health and safety, financial or other records to be retained for particular periods.
          </p>
          <p>
            Deletion of information from the active application may not result in immediate deletion from encrypted backups or other systems where information is retained temporarily for security, disaster recovery, legal or technical reasons.
          </p>
        </Section>

        <Section id="access-and-correction" title="18. Access and correction">
          <p>
            Individuals may have rights under the Privacy Act 2020 to request access to personal information held about them and to request correction of that information.
          </p>
          <p>Requests can be made using the contact details below.</p>
          <p>Before responding, we may need to verify the requester&apos;s identity.</p>
          <p>Where information is controlled by a Fieldlink customer, we may refer or coordinate the request with that customer where appropriate.</p>
          <p>There may be lawful grounds on which access or correction can be refused or limited.</p>
        </Section>

        <Section id="cookies" title="19. Cookies and technical information">
          <p>Fieldlink may use cookies, local storage and similar technologies where reasonably necessary to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>authenticate users;</li>
            <li>maintain sessions;</li>
            <li>remember settings;</li>
            <li>protect account security;</li>
            <li>understand application performance;</li>
            <li>diagnose errors; and</li>
            <li>improve the Services.</li>
          </ul>
          <p>Where required, additional information may be provided through a cookie notice or consent mechanism.</p>
        </Section>

        <Section id="children" title="20. Children">
          <p>Fieldlink is a business and workforce platform and is not intended as a general service for children.</p>
          <p>
            Customers must ensure that any personal information relating to young persons entered into Fieldlink is collected and used lawfully and appropriately.
          </p>
        </Section>

        <Section id="changes" title="21. Changes to this policy">
          <p>Fieldlink may update this Privacy Policy from time to time to reflect changes to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>the Services;</li>
            <li>technology;</li>
            <li>security practices;</li>
            <li>service providers; or</li>
            <li>legal requirements.</li>
          </ul>
          <p>The current version will be published on the Fieldlink website with its effective date.</p>
          <p>
            Where a change materially affects how we handle personal information, we will take reasonable steps to provide appropriate notice where required.
          </p>
        </Section>

        <Section id="privacy-officer" title="22. Privacy officer">
          <p>Fieldlink has a privacy contact responsible for privacy matters.</p>
          <p>Privacy enquiries, access requests, correction requests and privacy concerns can be sent to:</p>
          <p>
            Privacy Officer<br />
            Fieldlink<br />
            Email: <a href="mailto:Privacy@fieldlink.co.nz" className="underline">Privacy@fieldlink.co.nz</a><br />
            Website: <a href="https://www.fieldlink.co.nz" className="underline" rel="noreferrer" target="_blank">www.fieldlink.co.nz</a>
          </p>
          <p>Individuals may also have the right to make a complaint to the Office of the Privacy Commissioner in New Zealand.</p>
        </Section>

        <Section id="governing-law" title="23. Governing law">
          <p>This Privacy Policy is intended to operate consistently with the laws of New Zealand.</p>
          <p>Nothing in this Privacy Policy excludes, restricts or modifies any right, remedy, obligation or liability where doing so would be unlawful.</p>
        </Section>

        <Section id="contact" title="24. Contact">
          <p>For privacy or security enquiries:</p>
          <p>
            Fieldlink<br />
            New Zealand
          </p>
          <p>
            Privacy: <a href="mailto:Privacy@fieldlink.co.nz" className="underline">Privacy@fieldlink.co.nz</a><br />
            Website: <a href="https://www.fieldlink.co.nz" className="underline" rel="noreferrer" target="_blank">www.fieldlink.co.nz</a>
          </p>
        </Section>
      </LegalLayout>
    </>
  );
}
